import { Alert, Badge, Button, Card, DataTableV2, FormField, FormSurface, Input, Module, ModuleBody, ModuleHead, Select, Switch, useCollection } from "@vjal/ui";
import type { CollectionColumnDef, DataTableColumn } from "@vjal/ui";
import { useMemo, useState } from "react";
import { DisabledControlReason } from "../components/AccessState";
import { evaluateActionCapability } from "../domain/capabilities";
import { getRoleDefinition, type CoachingRole } from "../domain/roles";
import { useCoachingProfile, useProfileUpdateMutation } from "../hooks/useCoachingQueries";
import { isLiveCoachingDataMode } from "../hooks/useLiveProductData";
import type { CoachingProfileDto } from "../services/coaching-dtos";
import type { ProductScreenContext, ProductScreenContract, TableRow } from "./product-route-contracts";

const profileRows: TableRow[] = [
  { id: "name", field: "Display name", current: "Editable", access: "allowed", future: "localized preferred name" },
  { id: "title", field: "Title", current: "Editable for staff and company admin", access: "role-based", future: "company stakeholder title" },
  { id: "phone", field: "Phone", current: "Editable", access: "allowed", future: "SMS/reminder readiness" },
  { id: "timezone", field: "Timezone", current: "Editable", access: "allowed", future: "calendar default" },
  { id: "notifications", field: "Notification preferences", current: "Editable", access: "allowed", future: "digest preferences" },
  { id: "email", field: "Email", current: "Read-only", access: "locked", future: "auth-service handoff" },
  { id: "credentials", field: "Credentials", current: "Not shown", access: "locked", future: "auth/security handoff" },
  { id: "billing", field: "Billing and plan", current: "Not shown", access: "locked", future: "billing handoff" },
  { id: "permissions", field: "Permissions", current: "Outside profile", access: "blocked", future: "admin route only" }
];

export function profileSettingsScreen(): ProductScreenContract {
  return {
    title: (role) => `${getRoleDefinition(role).label} profile settings`,
    primaryJob: "Edit allowed self-service profile fields while keeping identity, billing, and permissions out of scope.",
    secondaryJob: "Email and permission changes are explicit handoffs, not inline profile mutations.",
    workTitle: "Profile field contract",
    rows: profileRows,
    columns: profileColumns(),
    states: ["loading", "editable", "locked-field", "read-only-handoff", "save-ready", "error"],
    apiFamilies: ["user-service", "auth-service"],
    roleDeltas: "All roles may edit allowed profile fields; permission and identity changes remain outside this screen.",
    extensionSlots: ["notification preferences", "calendar defaults", "credential handoff", "billing handoff"],
    mobilePosture: "Fields, switches, and save controls stack with locked email visible before handoff notes.",
    evidenceRequirement: "Playwright must prove editable fields, locked email, permission handoff, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: ProfileSettingsSurface
  };
}

const profileTimezoneOptions = ["America/New_York", "Europe/London", "Asia/Tokyo"];

export interface ProfileFormDefaults {
  displayName: string;
  title: string;
  phone: string;
  timezone: string;
  email: string;
  sessionReminders: boolean;
  calendarAlerts: boolean;
  weeklyDigest: boolean;
}

/**
 * Form defaults hydrate from the cached `/auth/me` (and post-save) profile
 * projection when fields are present. Evidence-mode placeholders remain
 * available for deterministic mock runs, but live mode never invents a value
 * for a missing profile field.
 */
export function selectProfileFormDefaults(
  profile: CoachingProfileDto | undefined,
  role: CoachingRole
): ProfileFormDefaults {
  const titleAllowed = role !== "participant";
  const live = isLiveCoachingDataMode();
  const preferences = profile?.notification_preferences;
  return {
    displayName: profile?.name?.trim() || (live ? "" : role === "participant" ? "Learner One" : "Admin Operator"),
    title: titleAllowed ? profile?.title?.trim() || (live ? "" : "Leadership stakeholder") : "",
    phone: profile?.phone?.trim() || (live ? "" : "+1 555 0100"),
    timezone:
      profile?.timezone && profileTimezoneOptions.includes(profile.timezone) ? profile.timezone : live ? "" : "America/New_York",
    email: profile?.email?.trim() || (live ? "" : "learner@example.com"),
    sessionReminders: preferences?.session_reminders ?? (live ? false : true),
    calendarAlerts: preferences?.calendar_alerts ?? (live ? false : true),
    weeklyDigest: preferences?.weekly_digest ?? false
  };
}

function ProfileSettingsSurface(context: ProductScreenContext) {
  const profileQuery = useCoachingProfile();
  const live = isLiveCoachingDataMode();

  // In live mode, never mount the editable form before /auth/me settles. This
  // prevents its evidence-mode defaults from becoming a real PATCH payload.
  if (live && (profileQuery.isLoading || profileQuery.isPending)) {
    return (
      <Module span={12}>
        <ModuleBody>
          <Alert variant="info">Loading your profile details.</Alert>
        </ModuleBody>
      </Module>
    );
  }
  if (live && (profileQuery.isError || !profileQuery.data)) {
    return (
      <Module span={12}>
        <ModuleBody>
          <Alert variant="error">Your profile details are unavailable right now. Try again later.</Alert>
        </ModuleBody>
      </Module>
    );
  }

  // Remounting on profile identity re-derives the lazy useState defaults when
  // the session profile hydrates or changes, without effect-driven setState.
  return <ProfileSettingsForm key={profileQuery.data?.id ?? "anonymous"} context={context} profile={profileQuery.data} />;
}

function ProfileSettingsForm({ context, profile }: { context: ProductScreenContext; profile?: CoachingProfileDto }) {
  const { role } = context;
  const roleDefinition = getRoleDefinition(role);
  const updateDecision = evaluateActionCapability(role, "profile:update");
  const titleAllowed = role !== "participant";
  const profileUpdate = useProfileUpdateMutation();
  const defaults = selectProfileFormDefaults(profile, role);
  const [displayName, setDisplayName] = useState(defaults.displayName);
  const [title, setTitle] = useState(defaults.title);
  const [phone, setPhone] = useState(defaults.phone);
  const [timezone, setTimezone] = useState(defaults.timezone);
  const [sessionReminders, setSessionReminders] = useState(defaults.sessionReminders);
  const [calendarAlerts, setCalendarAlerts] = useState(defaults.calendarAlerts);
  const [weeklyDigest, setWeeklyDigest] = useState(defaults.weeklyDigest);
  const errors = profileUpdate.data?.field_errors ?? {};
  const saveSucceeded = profileUpdate.data?.ok === true;
  const saveFailed = profileUpdate.data?.status === "validation_error";

  function saveProfile() {
    profileUpdate.mutate({
      name: displayName,
      title: titleAllowed ? title : undefined,
      phone,
      timezone,
      notification_preferences: {
        session_reminders: sessionReminders,
        calendar_alerts: calendarAlerts,
        weekly_digest: weeklyDigest
      }
    });
  }

  return (
    <>
      <div className="col-span-8">
        {/* F3: real @vjal/ui FormSurface for Form-1 profile /settings routes. */}
        <FormSurface
          title="Allowed profile fields"
          hideSubmit
          className="overflow-visible"
          error={
            saveFailed
              ? "Profile could not be saved. Review the highlighted fields before trying again."
              : undefined
          }
        >
          <p className="m-0 text-sm text-ink-muted">{`${roleDefinition.label} self-service profile contract.`}</p>
          {profileUpdate.isPending ? <Alert variant="info">Saving allowed profile fields.</Alert> : null}
          {saveSucceeded ? <Alert variant="success">Profile saved. Allowed fields are ready for the next refresh.</Alert> : null}
          <FormField label="Display name" hint="Allowed self-service field." error={errors.name}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            label="Title"
            hint={titleAllowed ? "Allowed for staff and company stakeholder profiles." : "Participant learner profile does not collect title."}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={titleAllowed ? title : "Not collected"}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!titleAllowed}
              />
            )}
          </FormField>
          <FormField label="Phone" hint="Allowed self-service field for contact and reminders." error={errors.phone}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            )}
          </FormField>
          <Select
            label="Timezone"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            hint="Allowed self-service field used by session reminders."
            error={errors.timezone}
          >
            <option value="">Select timezone…</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
          </Select>
          <FormField label="Email" hint="Identity changes route through the auth service.">
            {({ id, describedBy }) => (
              <Input id={id} aria-describedby={describedBy} value={defaults.email} readOnly />
            )}
          </FormField>
          <Card className="shadow-none">
            <div className="grid gap-3">
            <p className="m-0 text-sm font-semibold text-strong">Notification preferences</p>
            <Switch checked={sessionReminders} onChange={setSessionReminders} label="Session reminders" />
            <Switch checked={calendarAlerts} onChange={setCalendarAlerts} label="Calendar change alerts" />
            <Switch checked={weeklyDigest} onChange={setWeeklyDigest} label="Weekly digest" />
            </div>
          </Card>
          <div className="grid gap-2">
            <Button
              type="button"
              loading={profileUpdate.isPending}
              disabled={!updateDecision.allowed}
              onClick={saveProfile}
              aria-describedby={!updateDecision.allowed ? "profile-update-reason" : undefined}
            >
              Save allowed profile fields
            </Button>
            {!updateDecision.allowed ? (
              <DisabledControlReason id="profile-update-reason">{updateDecision.reason}</DisabledControlReason>
            ) : null}
          </div>
        </FormSurface>
      </div>
      <Module span={4} className="overflow-visible">
        <ModuleHead title="Handoffs" subtitle="Fields intentionally outside profile save." />
        <ModuleBody className="space-y-4">
          <Alert variant="info">Email, credentials, billing, and role grants are not edited on this screen.</Alert>
          <LockedField label="Email" reason="Email is owned by auth-service identity verification." />
          <LockedField label="Credentials" reason="Passwords, OTPs, tokens, and provider secrets never render in profile." />
          <LockedField label="Billing and plan" reason="Company admin and participant profiles expose no billing, contract, or subscription detail." />
          <Badge variant="warning">Permissions route only</Badge>
          <Badge variant="vjal">Allowed notification preferences</Badge>
        </ModuleBody>
      </Module>
      <Module span={12} className="overflow-visible">
        <ModuleHead title="Field permissions" subtitle="Profile field contract reference for this role." />
        <ModuleBody flush>
          <ProfilePermissionsTable rows={profileRows} />
        </ModuleBody>
      </Module>
    </>
  );
}

function LockedField({ label, reason }: { label: string; reason: string }) {
  return (
    <Card className="bg-whisper shadow-none">
      <div className="grid gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-strong">{label}</span>
        <Badge variant="warning">locked</Badge>
      </div>
      <DisabledControlReason>{reason}</DisabledControlReason>
      </div>
    </Card>
  );
}

function profileColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "field", header: "Field" },
    { key: "current", header: "Current behavior" },
    {
      key: "access",
      header: "Access",
      render: (row) => (
        <Badge variant={row.access === "allowed" ? "success" : row.access === "locked" ? "warning" : "default"}>
          {String(row.access)}
        </Badge>
      )
    },
    { key: "future", header: "Future extension" }
  ];
}

function profilePermissionTableColumns(): CollectionColumnDef<TableRow>[] {
  return [
    { id: "field", header: "Field", priority: 1 },
    { id: "current", header: "Current behavior", priority: 2 },
    {
      id: "access",
      header: "Access",
      priority: 2,
      cell: (row) => (
        <Badge variant={row.access === "allowed" ? "success" : row.access === "locked" ? "warning" : "default"}>
          {String(row.access)}
        </Badge>
      )
    },
    { id: "future", header: "Future extension", priority: 3 }
  ];
}

function ProfilePermissionsTable({ rows }: { rows: TableRow[] }) {
  const columns = useMemo(() => profilePermissionTableColumns(), []);
  const collection = useCollection<TableRow>({
    mode: "client",
    rows,
    columns,
    urlSync: false,
    getRowKey: (row) => row.id,
  });
  return <DataTableV2 collection={collection} caption="Profile field permissions" />;
}
